import InfoOutlined from "@mui/icons-material/InfoOutlined";
import type { SwitchProps } from "@mui/material";
import {
  Box,
  IconButton,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";

type SwitchButtonProps = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  info?: string;
  disabled?: boolean;
  disabledReason?: string;
  switchInputProps?: SwitchProps["inputProps"];
};

const inactiveSwitchColor = "#6b7280";
const activeSwitchColor = "#074ee8";
const activeTrackBackground = "rgba(7, 78, 232, 0.08)";

const switchSx = {
  width: 28,
  height: 16,
  padding: 0,
  overflow: "visible",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "3px",
    transitionDuration: "180ms",
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: activeSwitchColor,
      "& .MuiSwitch-thumb": {
        backgroundColor: activeSwitchColor,
      },
      "& + .MuiSwitch-track": {
        backgroundColor: activeTrackBackground,
        borderColor: activeSwitchColor,
        opacity: 1,
      },
    },
    "&.Mui-disabled": {
      color: inactiveSwitchColor,
      opacity: 0.4,
      "& .MuiSwitch-thumb": {
        backgroundColor: inactiveSwitchColor,
      },
      "& + .MuiSwitch-track": {
        backgroundColor: "#ffffff",
        borderColor: inactiveSwitchColor,
        opacity: 0.4,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 10,
    height: 10,
    backgroundColor: inactiveSwitchColor,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    width: "100%",
    height: "100%",
    backgroundColor: "#ffffff",
    border: `1px solid ${inactiveSwitchColor}`,
    borderRadius: "999px",
    boxSizing: "border-box",
    opacity: 1,
  },
};

export function SwitchButton({
  checked,
  disabled = false,
  disabledReason,
  info,
  label,
  onChange,
  switchInputProps,
}: SwitchButtonProps) {
  const [isDisabledTooltipOpen, setIsDisabledTooltipOpen] = useState(false);
  const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false);
  const disabledTooltipTitle = disabled && disabledReason ? disabledReason : "";
  const isDisabledTooltipAvailable = Boolean(disabledTooltipTitle);
  return (
    <Tooltip
      title={disabledTooltipTitle}
      disableFocusListener={!isDisabledTooltipAvailable}
      disableHoverListener={!isDisabledTooltipAvailable}
      disableTouchListener={!isDisabledTooltipAvailable}
      open={
        isDisabledTooltipAvailable &&
        isDisabledTooltipOpen &&
        !isInfoTooltipOpen
      }
      onClose={() => setIsDisabledTooltipOpen(false)}
      onOpen={() => setIsDisabledTooltipOpen(true)}
    >
      <span>
        <MenuItem
          component="div"
          role="menuitemcheckbox"
          aria-checked={checked}
          aria-disabled={disabled ? true : undefined}
          onClick={() => {
            if (!disabled) {
              onChange(!checked);
            }
          }}
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            m: "4px 6px",
            minHeight: "auto",
            p: "6px 10px",
            color: disabled ? "text.disabled" : "text.primary",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={400}
              sx={{ fontSize: "14px" }}
            >
              {label}
            </Typography>
            {info ? (
              <Tooltip
                describeChild
                title={info}
                open={isInfoTooltipOpen}
                onClose={() => setIsInfoTooltipOpen(false)}
                onOpen={() => setIsInfoTooltipOpen(true)}
              >
                <IconButton
                  aria-label={`About ${label}`}
                  size="small"
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    color: "text.secondary",
                    flexShrink: 0,
                    p: 0.25,
                    "& .MuiSvgIcon-root": {
                      fontSize: "12px",
                      marginBottom: "4px",
                    },
                  }}
                >
                  <InfoOutlined fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          <Switch
            checked={checked}
            disabled={disabled}
            disableRipple
            size="small"
            inputProps={{
              "aria-label": label,
              tabIndex: -1,
              ...switchInputProps,
            }}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              onChange(event.target.checked);
            }}
            sx={switchSx}
          />
        </MenuItem>
      </span>
    </Tooltip>
  );
}
